import { Play } from "lucide-react";
import type { ApiProductImage } from "@/lib/api";
import { clampIndex } from "@/lib/product-images";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const isVideo = (m: ApiProductImage) => m.mediaType === "video";

/**
 * Large image with a thumbnail strip: down the left on desktop, under the
 * image on smaller screens where a column would steal width from the picture.
 *
 * Additional photos may be videos. One plays in the large frame, muted with
 * its controls showing, and only once it is the frame on screen; its
 * thumbnail is its first frame with a play mark.
 */
export function ProductGallery({
  images,
  index,
  onIndex,
  name,
  stacked,
}: {
  images: ApiProductImage[];
  index: number;
  onIndex: (i: number) => void;
  name: string;
  /** Thumbnails always under the picture, smaller: for a narrow column like the quick-add sheet. */
  stacked?: boolean;
}) {
  const current = clampIndex(index, images.length);
  const shown = images[current];

  return (
    <section aria-label="Galeri produk" className={cn("flex flex-col gap-3", !stacked && "lg:flex-row-reverse")}>
      <div className="min-w-0 flex-1">
        <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-cream">
          {shown && isVideo(shown) ? (
            <video
              key={shown.id}
              className="size-full bg-black object-contain"
              src={shown.publicUrl}
              aria-label={shown.alt ?? `Video ${name}`}
              controls
              muted
              playsInline
              loop
              // Plays on arrival unless the shopper asked for less motion; the
              // controls are there either way.
              autoPlay={!prefersReducedMotion()}
              preload="metadata"
            />
          ) : shown ? (
            <img className="size-full object-contain" src={shown.publicUrl} alt={shown.alt ?? name} />
          ) : (
            <div className="flex size-full items-center justify-center p-8 text-ink/60">
              Foto produk belum tersedia.
            </div>
          )}
        </div>
      </div>

      {images.length > 1 && (
        <div
          role="tablist"
          aria-label="Foto produk"
          aria-orientation="horizontal"
          className={cn(
            "flex gap-2 overflow-x-auto pb-1",
            !stacked &&
              "lg:max-h-[min(70vh,36rem)] lg:w-20 lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:pb-0 lg:pr-1",
          )}
        >
          {images.map((im, i) => (
            <Button
              key={im.id}
              type="button"
              variant="ghost"
              role="tab"
              aria-selected={i === current}
              aria-label={isVideo(im) ? `Video ${i + 1}` : `Foto ${i + 1}`}
              onClick={() => onIndex(i)}
              className={cn(
                "relative shrink-0 overflow-hidden rounded-xl border-2 bg-cream p-0 hover:bg-cream",
                stacked ? "size-14" : "size-20",
                i === current ? "border-brand" : "border-transparent hover:border-ink/20",
              )}
            >
              {isVideo(im) ? (
                <>
                  {/* #t makes Safari paint the first frame instead of nothing. */}
                  <video
                    className="pointer-events-none size-full bg-black object-cover"
                    src={`${im.publicUrl}#t=0.1`}
                    muted
                    playsInline
                    preload="metadata"
                    aria-hidden
                    tabIndex={-1}
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-ink/20">
                    <Play className="size-5 fill-white text-white" />
                  </span>
                </>
              ) : (
                <img className="size-full object-cover" src={im.publicUrl} alt="" loading="lazy" />
              )}
            </Button>
          ))}
        </div>
      )}
    </section>
  );
}

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
